/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { Spin, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import DOMPurify from 'dompurify';
import './lifeEventView.scss';
import Main from '../../components/layout/Main';
import SEO from '../../components/SEO';
import leftArrow from '../../assets/images/left-nav-arrow.svg';
import rightArrow from '../../assets/images/right-nav-arrow.svg';
import { getSelectedTranslation, getTranslation } from '../../core/utils/helpers';
import useLanguage from '../../core/language/useLanguage';
import { getYearlyLifeEventRequest, resetYearlyLifeEvent } from '../../core/horoscope/horoscopeActions';
import useUser from '../../core/user/useUser';
import YellowThemeButton from '../../components/YellowThemeButton';
import history from '../../core/utils/history';
import app from '../../core/constants';
import AiChat from '../../components/AiChat';
import FormattedText from '../../components/AiChat/FormattedText';
import useAi from '../../core/ai/useAi';
import { QUESTION_LIMIT } from '../../core/ai/aiReducer';
import { BlingIcon } from '../../components/icons';
import Accordion from '../../components/Accordion';

const LifeEventView = () => {
    const { sign, year, partSlug } = useParams();
    const { language } = useLanguage();
    const { user } = useUser();
    const dispatch = useDispatch();

    const { yearlyLifeEvent, isFetchingLifeEvent, lifeEventError } = useSelector(state => state.horoscope);

    // AI Integration
    const { ai, aiSummaryRequest, aiQuestionRequest, aiClearChat, rateLimitInfo } = useAi();
    const [questionsAsked, setQuestionsAsked] = useState(0);
    const [showAiChat, setShowAiChat] = useState(false);

    // Fetch data on mount and when params change
    useEffect(() => {
        if (sign && year && partSlug) {
            dispatch(getYearlyLifeEventRequest({
                sign,
                year,
                partSlug,
                userAuthenticated: user?.isLoggedIn,
            }));
        }

        return () => {
            dispatch(resetYearlyLifeEvent());
        };
    }, [sign, year, partSlug, user?.isLoggedIn, dispatch]);

    // Scroll to top when part changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [partSlug]);

    // Clear AI chat when life event changes
    useEffect(() => {
        aiClearChat();
        setQuestionsAsked(0);
        setShowAiChat(false);
    }, [sign, year, partSlug]);

    // Get translation for current life event
    const getLifeEventTranslation = () => {
        if (!yearlyLifeEvent?.lifeEvent?.translations) return null;
        return getSelectedTranslation(yearlyLifeEvent.lifeEvent.translations, language?.selectedLanguage?.id);
    };

    // Get horoscope display name
    const getHoroscopeName = () => {
        if (!yearlyLifeEvent?.horoscope) return '';
        return `${yearlyLifeEvent.horoscope.sign_name} ${yearlyLifeEvent.horoscope.year}`;
    };

    // Navigate to another part
    const navigateToPart = (slug) => {
        history.push(`/horoscope/yearly/${sign}/${year}/${slug}`);
    };

    // Get previous and next parts for navigation
    const getNavigation = () => {
        const allParts = yearlyLifeEvent?.allParts || [];
        if (!allParts.length || !yearlyLifeEvent?.lifeEvent) return { prev: null, next: null };

        const currentIndex = allParts.findIndex(p => p.slug === yearlyLifeEvent.lifeEvent.slug);

        return {
            prev: currentIndex > 0 ? allParts[currentIndex - 1] : null,
            next: currentIndex < allParts.length - 1 ? allParts[currentIndex + 1] : null,
        };
    };

    // Handle buy button click
    const handleBuyClick = () => {
        if (!user?.isLoggedIn) {
            history.push(`/signup?redirect=/horoscope/yearly/${sign}/${year}/${partSlug}`);
            return;
        }
        history.push('/life-events');
    };

    // AI Helper Functions - Get context including all life event parts
    const getAllLifeEventsText = () => {
        const allParts = yearlyLifeEvent?.allParts || [];
        const currentTranslation = getLifeEventTranslation();
        const currentSlug = yearlyLifeEvent?.lifeEvent?.slug;
        const horoscopeName = getHoroscopeName();

        let text = `=== ${horoscopeName} Life Events ===\n\n`;

        // List all available life event topics
        text += 'Available Life Event Topics:\n';
        allParts.forEach((part, index) => {
            const isCurrent = part.slug === currentSlug;
            text += `${index + 1}. ${part.name}${isCurrent ? ' (Currently Viewing)' : ''}\n`;
        });

        // Add detailed content for current life event
        text += `\n\n=== Detailed Content: ${currentTranslation?.name || ''} ===\n`;
        if (currentTranslation?.short_description) {
            text += `${currentTranslation.short_description}\n\n`;
        }
        if (currentTranslation?.description) {
            text += `${currentTranslation.description}`;
        }

        return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
    };

    // Get current life event text only (for summary)
    const getCurrentLifeEventText = () => {
        const translation = getLifeEventTranslation();
        if (!translation) return '';

        let text = `${translation?.name || ''}\n`;
        if (translation?.short_description) {
            text += `${translation.short_description}\n`;
        }
        if (translation?.description) {
            text += `${translation.description}`;
        }
        return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
    };

    const handleGetSummary = () => {
        // Summary focuses on current life event
        const content = getCurrentLifeEventText();
        if (content) {
            aiSummaryRequest({ content });
        }
    };

    const openAiChat = () => {
        if (!ai.get('summary')) {
            handleGetSummary();
        }
        setShowAiChat(true);
    };

    const closeAiChat = () => {
        setShowAiChat(false);
    };

    const handleAskQuestion = (question) => {
        // Q&A uses all life events context for broader queries
        aiQuestionRequest({ question, content: getAllLifeEventsText() });
        setQuestionsAsked(prev => prev + 1);
    };

    const handleCopySummary = () => {
        const summary = ai.get('summary');
        if (summary) {
            window.navigator.clipboard.writeText(summary).then(() => {
                message.success('Summary copied to clipboard');
            }).catch(() => {
                message.error('Failed to copy');
            });
        }
    };

    // Question limit applies to everyone
    const questionLimit = QUESTION_LIMIT;

    // Rate limit message
    const getRateLimitMessage = () => {
        if (rateLimitInfo.isDailyLimitReached) {
            return 'Daily limit reached (50 requests/day). Please try again tomorrow.';
        }
        if (rateLimitInfo.isHourlyLimitReached) {
            return 'Hourly limit reached (10 requests/hour). Please try again later.';
        }
        return '';
    };

    // Loading state
    if (isFetchingLifeEvent) {
        return (
            <Main
                className="purchase-life-events center-align"
                title={<div>Loading...</div>}
                headerTitle={(
                    <div>
                        <img style={{ height: 70, width: 70 }}
                            src="https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg"
                            alt="logo" />
                    </div>
                )}
                menuWidth={350}
                menuPlacement="right"
                showHeader
                displayRightMenu
                showFooter={false}>
                <div className="loaderSpacer">
                    <Spin size="large" />
                </div>
            </Main>
        );
    }

    // Error state
    if (lifeEventError) {
        return (
            <Main
                className="purchase-life-events center-align"
                title={<div>Error</div>}
                headerTitle={(
                    <div>
                        <img style={{ height: 70, width: 70 }}
                            src="https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg"
                            alt="logo" />
                    </div>
                )}
                menuWidth={350}
                menuPlacement="right"
                showHeader
                displayRightMenu
                showFooter={false}>
                <div className="display-life-event">
                    <h2>{getTranslation(language, 'error', 'Error')}</h2>
                    <p>{lifeEventError}</p>
                    <YellowThemeButton onClick={() => history.push('/')}>
                        {getTranslation(language, 'go_home', 'Go Home')}
                    </YellowThemeButton>
                </div>
            </Main>
        );
    }

    // No data state
    if (!yearlyLifeEvent) {
        return null;
    }

    const lifeEventTranslation = getLifeEventTranslation();
    const { prev, next } = getNavigation();
    const userHasAccess = app.ENABLE_FREE_PREMIUM || yearlyLifeEvent?.lifeEvent?.user_has_access;
    const horoscopeName = getHoroscopeName();
    const pricing = yearlyLifeEvent?.pricing;

    // Get special forecasts from yearly horoscope data
    const specialForecasts = yearlyLifeEvent?.horoscope?.horoscopeData?.specialForecasts
        || yearlyLifeEvent?.horoscope?.horoscopeData?.special_forecasts || [];

    // Helper function to validate YouTube video code
    const isValidYouTubeVideo = (code) => {
        if (!code) return false;
        // Reject if it looks like a numeric ID (not a YouTube video ID)
        if (code.length === 9 && /^[0-9]+$/.test(code)) {
            return false;
        }
        // Reject if it contains slashes (likely a URL path, not a video ID)
        if (code.indexOf('/') > -1) {
            return false;
        }
        return true;
    };

    // Render Special Forecasts section
    const renderSpecialForecasts = () => {
        if (!specialForecasts || specialForecasts.length === 0) {
            return null;
        }

        return (
            <div className="special-forecasts-container">
                <h2 className="special-forecasts-title">
                    {getTranslation(language, 'special_forecasts', 'Special Forecasts')}
                </h2>
                {specialForecasts.map((forecast) => {
                    // Get translation for current language
                    const forecastTranslation = getSelectedTranslation(
                        forecast?.translations,
                        language?.selectedLanguage?.id,
                    );

                    // Check if valid video exists
                    const forecastVideoCode = forecastTranslation?.video_link;
                    const hasVideo = forecastVideoCode && isValidYouTubeVideo(forecastVideoCode);

                    return (
                        <Accordion
                            key={forecast.id}
                            title={forecastTranslation?.name || forecast.name || 'Forecast'}>
                            {/* Video (if exists) */}
                            {hasVideo && (
                                <div className="accordion__video-block">
                                    <iframe
                                        src={`https://www.youtube.com/embed/${forecastVideoCode}`}
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        referrerPolicy="strict-origin-when-cross-origin" />
                                </div>
                            )}

                            {/* Description text */}
                            {forecastTranslation?.description && (
                                <div
                                    className="special-forecast-description"
                                    // eslint-disable-next-line
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(forecastTranslation.description),
                                    }} />
                            )}
                        </Accordion>
                    );
                })}
            </div>
        );
    };

    // SEO description
    const seoDescription = lifeEventTranslation?.short_description
        ? `${lifeEventTranslation.short_description.substring(0, 150)}...`
        : `${lifeEventTranslation?.name || ''} - ${horoscopeName} Vedic Astrology from Prakash Astrologer.`;

    return (
        <>
            <SEO
                title={`${lifeEventTranslation?.name || partSlug} - ${horoscopeName}`}
                description={seoDescription}
                url={`/horoscope/yearly/${sign}/${year}/${partSlug}`}
                type="article"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'Article',
                    headline: `${lifeEventTranslation?.name} - ${horoscopeName}`,
                    description: seoDescription,
                    author: {
                        '@type': 'Person',
                        name: 'Prakash Astrologer',
                    },
                    publisher: {
                        '@type': 'Organization',
                        name: 'Prakash Astrologer',
                    },
                }} />
            <Main
                className="purchase-life-events center-align"
                headerTitle={(
                    <div>
                        <img style={{ height: 70, width: 70 }}
                            src="https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg"
                            alt="logo" />
                    </div>
                )}
                menuWidth={350}
                menuPlacement="right"
                showHeader
                displayRightMenu
                showFooter={false}
                backLink={(
                    <div style={{ display: 'flex' }}>
                        <Link to={`/life-events/${sign}`} className="backlink" style={{ marginBottom: '24px' }} />
                    </div>
                )}>
                <div className="display-life-event">
                    {(prev || next) && (
                        <nav>
                            {prev ? (
                                <div onClick={() => navigateToPart(prev.slug)}>
                                    <img src={leftArrow} alt="previous" />
                                    <span>{prev.name}</span>
                                </div>
                            ) : <div />}
                            {next ? (
                                <div onClick={() => navigateToPart(next.slug)}>
                                    <span>{next.name}</span>
                                    <img src={rightArrow} alt="next" />
                                </div>
                            ) : <div />}
                        </nav>
                    )}

                    <h1>{lifeEventTranslation?.name}</h1>
                    <p>{horoscopeName}</p>

                    <div className="content-wrapper">
                        {/* User has access - show full content */}
                        {userHasAccess && (
                            <>
                                {lifeEventTranslation?.video_link && (
                                    <div className="video-block">
                                        <iframe
                                            src={`https://www.youtube.com/embed/${lifeEventTranslation.video_link}`}
                                            title="YouTube video player"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            referrerPolicy="strict-origin-when-cross-origin" />
                                    </div>
                                )}
                                {lifeEventTranslation?.description && (
                                    // eslint-disable-next-line
                                    <div dangerouslySetInnerHTML={{ __html: lifeEventTranslation.description }} />
                                )}
                                {/* Special Forecasts Section */}
                                {renderSpecialForecasts()}
                            </>
                        )}

                        {/* User doesn't have access - show locked overlay with blurred content */}
                        {!userHasAccess && (
                            <div className="locked-content-wrapper">
                                <div className="locked-overlay">
                                    <h3>{getTranslation(language, 'content_locked', 'This Content is Locked')}</h3>
                                    <p>{getTranslation(language, 'purchase_to_view', 'Purchase this life event to view the full content')}</p>
                                    <div className="pricing-info">
                                        <p className="price">
                                            {getTranslation(language, 'price', 'Price')}: £{yearlyLifeEvent?.lifeEvent?.current_price}
                                        </p>
                                        {pricing?.full_package_price && (
                                            <p className="package-price">
                                                {getTranslation(language, 'or_full_package', 'Or get all 15 Life Events')}: £{pricing.full_package_price}
                                            </p>
                                        )}
                                    </div>
                                    <YellowThemeButton onClick={handleBuyClick}>
                                        {user?.isLoggedIn
                                            ? getTranslation(language, 'buy_now', 'Buy Now')
                                            : getTranslation(language, 'sign_up_to_buy', 'Sign Up to Buy')}
                                    </YellowThemeButton>
                                </div>
                                <div className="blurred-preview">
                                    {lifeEventTranslation?.video_link_preview && (
                                        <div className="video-block">
                                            <iframe
                                                src={`https://www.youtube.com/embed/${lifeEventTranslation.video_link_preview}`}
                                                title="YouTube video player"
                                                frameBorder="0"
                                                referrerPolicy="strict-origin-when-cross-origin" />
                                        </div>
                                    )}
                                    <p>{lifeEventTranslation?.short_description}</p>
                                    <p>{lifeEventTranslation?.short_description}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Main>

            {/* AI Floating Button - only show when user has access */}
            {userHasAccess && (
                <button
                    type="button"
                    className="floating-ai-button"
                    onClick={openAiChat}>
                    <div className="floating-ai-button-text">
                        Summarize <BlingIcon />
                    </div>
                </button>
            )}

            {/* AI Chat Panel */}
            {showAiChat && (
                <div className="floating-ai-chat">
                    <div className="floating-ai-chat-header">
                        <span className="floating-ai-chat-title">AI Summary</span>
                        <button
                            type="button"
                            className="floating-ai-chat-close"
                            onClick={closeAiChat}>
                            &times;
                        </button>
                    </div>

                    {ai.get('isLoadingSummary') && (
                        <div className="ai-loading">
                            <Spin size="small" />
                            <span>AI is thinking...</span>
                        </div>
                    )}

                    {ai.get('error') && !ai.get('isLoadingSummary') && (
                        <div className="ai-error">
                            <p>{ai.get('error')}</p>
                            <button
                                type="button"
                                className="retry-btn"
                                onClick={handleGetSummary}>
                                Retry
                            </button>
                        </div>
                    )}

                    {ai.get('summary') && (
                        <div className="ai-summary-wrapper">
                            <div className="ai-summary">
                                <FormattedText text={ai.get('summary')} />
                            </div>
                            <button
                                type="button"
                                className="copy-summary-btn"
                                onClick={handleCopySummary}
                                title="Copy summary">
                                <CopyOutlined />
                            </button>
                        </div>
                    )}

                    {ai.get('summary') && (
                        <AiChat
                            chatHistory={ai.get('chatHistory')}
                            onSend={handleAskQuestion}
                            isLoading={ai.get('isLoadingAnswer')}
                            questionLimit={questionLimit}
                            questionsAsked={questionsAsked}
                            isRateLimited={rateLimitInfo.isLimitReached}
                            rateLimitMessage={getRateLimitMessage()} />
                    )}
                </div>
            )}
        </>
    );
};

export default LifeEventView;
